import { useSessionNotification } from '../../../utils/actionableNotifications';
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { useAuth } from '../../../components/contexts/AuthContext';
import { useSocket } from '../../../components/contexts/SocketContext';
import { useAccess } from '../../access/context/AccessProvider';
import { api, command, gateActive, inSession, needsExplanation } from '../models/attendance';
import AppTheme from '../../../components/theme/AppTheme';

const Context = createContext(null);
export function AttendanceProvider({ children }) {
  const { authState } = useAuth(), { socket } = useSocket(), { can } = useAccess();
  const enabled = authState?.isAuthenticated && can('attendance.view_own');
  const identity = `${authState?.id}:${authState?.token}:${authState?.session_id}`;
  const [resource, setResource] = useState(null), [error, setError] = useState(null), [prompt, setPrompt] = useState(null);
  const [directoryRevision, invalidateDirectory] = useState(0);
  const state = resource?.identity === identity ? resource.value : null;
  const revision = useRef(0), previous = useRef(null), retry = useRef(null), failures = useRef(0), live = useRef(true);
  const [notices, noticeContext] = useSessionNotification();
  const reload = useCallback(async () => {
    if (!enabled || !live.current) return null;
    const request = ++revision.current;
    clearTimeout(retry.current);
    try {
      const next = await api.state();
      if (typeof next?.enabled !== 'boolean' || typeof next?.tracked !== 'boolean' || typeof next?.next_action !== 'string') throw new Error('Invalid attendance state response.');
      if (!live.current || request !== revision.current) return null;
      if (needsExplanation(previous.current, next)) setPrompt({ kind: previous.current.state, period_start: previous.current.since, period_end: next.since, session_id: previous.current.session?.id ?? null });
      previous.current = next;
      setResource({ identity, value: next }); setError(null); failures.current = 0;
      return next;
    } catch (failure) {
      if (!live.current || request !== revision.current) return null;
      setError(failure);
      retry.current = setTimeout(() => reload(), Math.min(60000, 2000 * 2 ** failures.current++));
      return null;
    }
  }, [enabled, identity]);
  useEffect(() => {
    const requests = revision;
    live.current = true; previous.current = null; setPrompt(null); setResource(null); reload();
    return () => { live.current = false; requests.current++; clearTimeout(retry.current); };
  }, [reload]);
  useEffect(() => {
    if (!socket || !enabled) return undefined;
    const self = data => { invalidateDirectory(value => value + 1); if (Number(data?.user_id) === Number(authState.id)) reload(); };
    const settings = data => {
      invalidateDirectory(value => value + 1);
      if (data?.enabled === false) { previous.current = null; setPrompt(null); setResource(current => current?.identity === identity ? { identity, value: { ...current.value, enabled: false } } : current); }
      reload();
    };
    const overrun = data => { if (previous.current?.enabled && previous.current?.tracked && Number(data?.user_id) === Number(authState.id)) notices.warning({ message: 'Your break has run over', description: 'Please return to work or check your remaining allowance.' }); };
    const reconnect = () => { invalidateDirectory(value => value + 1); reload(); };
    socket.on('connect', reconnect); socket.on('attendance:state_changed', self); socket.on('attendance:settings_changed', settings); socket.on('attendance:break_overrun', overrun);
    return () => { socket.off('connect', reconnect); socket.off('attendance:state_changed', self); socket.off('attendance:settings_changed', settings); socket.off('attendance:break_overrun', overrun); };
  }, [socket, enabled, authState.id, identity, reload, notices]);
  useEffect(() => {
    if (!socket || !state?.enabled || !state?.tracked) return undefined;
    let hadInput = false, lastMove = 0;
    const input = event => {
      if (document.visibilityState !== 'visible') return;
      if (event.type === 'mousemove') { if (Date.now() - lastMove < 1000) return; lastMove = Date.now(); }
      hadInput = true;
    };
    const events = ['keydown', 'mousedown', 'wheel', 'touchstart', 'mousemove'];
    events.forEach(name => window.addEventListener(name, input, { passive: true }));
    const timer = setInterval(() => { if (socket.connected) { socket.emit('attendance:heartbeat', { had_input: hadInput }); hadInput = false; } }, 30000);
    return () => { clearInterval(timer); events.forEach(name => window.removeEventListener(name, input)); };
  }, [socket, state?.enabled, state?.tracked]);
  const execute = useCallback(async (method, path, body, key) => {
    const startedAtRevision = revision.current;
    try {
      const result = await command(method, path, body, key)();
      if (live.current && revision.current === startedAtRevision && Number(result?.user_id) === Number(authState.id) && typeof result.enabled === 'boolean' && typeof result.tracked === 'boolean' && typeof result.next_action === 'string') {
        previous.current = result; setResource({ identity, value: result });
      }
      await reload(); return result;
    } catch (failure) { if (failure.code === 'ATTENDANCE_VERSION_CONFLICT') await reload(); throw failure; }
  }, [authState.id, identity, reload]);
  return <Context.Provider value={{ state, error, reload, directoryRevision, gateActive: gateActive(state), inSession: inSession(state), execute, prompt: state?.enabled && state?.tracked ? prompt : null, dismissPrompt: () => setPrompt(null) }}><AppTheme>{noticeContext}</AppTheme>{children}</Context.Provider>;
}
export const useAttendance = () => useContext(Context);
